import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import { CreditCard, Apple, Shield, ShieldCheck, ShieldAlert, CheckCircle2, ChevronRight, Truck, MapPin, Clock, ArrowLeft, Lock, AlertTriangle, Sparkles } from 'lucide-react';
import { detectServiceFromPrompt, getServiceConfig, ServiceId } from '../lib/serviceData';
import { createBooking, payAndStart } from '../lib/activityStore';
import { RequestBrief } from '../components/RequestBrief';
type PayMethod = 'card' | 'apple' | 'tabby' | 'gpay';
export function Payment() {
  const location = useLocation();
  const navigate = useNavigate();
  const stateService = location.state?.service as ServiceId | undefined;
  const supplierFromState = location.state?.supplier as {
    name: string;
    price: number;
    truck: string;
  } | undefined;
  const statePrompt = location.state?.prompt as string | undefined;
  const statePhotos = location.state?.photos as string[] | undefined;
  const photos: string[] = useMemo(() => {
    if (statePhotos && statePhotos.length) return statePhotos;
    if (typeof window === 'undefined') return [];
    try {
      const raw = sessionStorage.getItem('pair2.lastPhotos');
      return raw ? JSON.parse(raw) as string[] : [];
    } catch {
      return [];
    }
  }, [statePhotos]);
  const resolvedServiceId: ServiceId = stateService || (statePrompt ? detectServiceFromPrompt(statePrompt) : undefined) || (typeof window !== 'undefined' ? sessionStorage.getItem('pair2.activeService') as ServiceId || 'home' : 'home');
  const service = useMemo(() => getServiceConfig(resolvedServiceId), [resolvedServiceId]);
  const supplier = supplierFromState || {
    name: service.suppliers[0].name,
    price: service.suppliers[0].price,
    truck: service.suppliers[0].truck
  };
  const [method, setMethod] = useState<PayMethod>('card');
  const [promo, setPromo] = useState('');
  const [promoApplied, setPromoApplied] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [cardNumber, setCardNumber] = useState('4242 4242 4242 4242');
  const [cardName, setCardName] = useState('Sara Al Mansoori');
  const [expiry, setExpiry] = useState('12 / 28');
  const [cvv, setCvv] = useState('123');
  const [insured, setInsured] = useState(false);
  const subtotal = supplier.price;
  const serviceFee = Math.round(subtotal * 0.05);
  const insuranceFee = Math.max(15, Math.round(subtotal * 0.03 / 5) * 5);
  const vat = Math.round((subtotal + serviceFee + (insured ? insuranceFee : 0)) * 0.05);
  const promoDiscount = promoApplied ? 30 : 0;
  const total = subtotal + serviceFee + (insured ? insuranceFee : 0) + vat - promoDiscount;
  // If an existing job id is passed in (e.g. accepting a bid), we finalize that job
  // instead of creating a brand-new booking record.
  const existingJobId = location.state?.jobId as string | undefined;
  const handlePay = () => {
    setProcessing(true);
    let jobId = existingJobId;
    if (!jobId) {
      const job = createBooking({
        serviceId: service.id,
        serviceName: service.name,
        prompt: statePrompt || service.defaultPrompt,
        routeHeadline: service.summary.routeHeadline,
        photos,
        pickup: service.summary.pickup,
        dropoff: service.summary.dropoff,
        supplierName: supplier.name,
        supplierTruck: supplier.truck,
        price: total
      });
      jobId = job.id;
    }
    setTimeout(() => {
      payAndStart(jobId!);
      navigate('/tracking', {
        state: {
          service: service.id,
          jobId,
          prompt: statePrompt
        }
      });
    }, 1300);
  };
  return <div className="min-h-[calc(100vh-5rem)] bg-surface-100 py-10" data-id="element-2257">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2258">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-gray-900 mb-6 transition-colors" data-id="element-2259">
          <ArrowLeft className="w-4 h-4" data-id="element-2260" /> Back to carriers
        </button>

        <div className="mb-8" data-id="element-2261">
          <div className="inline-flex items-center gap-2 bg-brand-50 text-brand-700 px-3 py-1 rounded-full text-xs font-semibold mb-3" data-id="element-2262">
            Step 3 of 3 · Secure checkout
          </div>
          <h1 className="text-3xl font-display font-bold text-gray-900" data-id="element-2263">
            Confirm & pay
          </h1>
          <p className="text-gray-500 mt-1" data-id="element-2264">
            Your job starts the moment payment clears.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6" data-id="element-2265">
          {/* PAYMENT METHODS */}
          <div className="lg:col-span-3 space-y-6" data-id="element-2266">
            {/* Method selector */}
            <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6" data-id="element-2267">
              <h3 className="font-bold text-gray-900 mb-4" data-id="element-2268">Payment method</h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-id="element-2269">
                <MethodPill active={method === 'card'} onClick={() => setMethod('card')} icon={<CreditCard className="w-5 h-5" data-id="element-2271" />} label="Card" data-id="element-2270" />
                <MethodPill active={method === 'apple'} onClick={() => setMethod('apple')} icon={<Apple className="w-5 h-5" data-id="element-2273" />} label="Apple Pay" data-id="element-2272" />
                <MethodPill active={method === 'tabby'} onClick={() => setMethod('tabby')} icon={<span className="font-bold text-sm" data-id="element-2275">tabby</span>} label="4 payments" data-id="element-2274" />
                <MethodPill active={method === 'gpay'} onClick={() => setMethod('gpay')} icon={<span className="text-lg font-bold leading-none" data-id="element-2277">
                      <span className="text-[#4285F4]" data-id="element-2278">G</span>
                      <span className="text-[#EA4335]" data-id="element-2279">o</span>
                      <span className="text-[#FBBC05]" data-id="element-2280">o</span>
                      <span className="text-[#4285F4]" data-id="element-2281">g</span>
                      <span className="text-[#34A853]" data-id="element-2282">l</span>
                      <span className="text-[#EA4335]" data-id="element-2283">e</span>
                    </span>} label="Google Pay" data-id="element-2276" />
              </div>

              {/* Card form */}
              {method === 'card' && <motion.div initial={{
              opacity: 0,
              y: 6
            }} animate={{
              opacity: 1,
              y: 0
            }} className="mt-5 space-y-3" data-id="element-2284">
                  <Field label="Card number" data-id="element-2285">
                    <input value={cardNumber} onChange={e => setCardNumber(e.target.value)} className="w-full bg-transparent outline-none text-sm font-medium text-gray-900 tracking-wider" placeholder="4242 4242 4242 4242" data-id="element-2286" />
                  </Field>
                  <Field label="Name on card" data-id="element-2287">
                    <input value={cardName} onChange={e => setCardName(e.target.value)} className="w-full bg-transparent outline-none text-sm font-medium text-gray-900" data-id="element-2288" />
                  </Field>
                  <div className="grid grid-cols-2 gap-3" data-id="element-2289">
                    <Field label="Expiry" data-id="element-2290">
                      <input value={expiry} onChange={e => setExpiry(e.target.value)} className="w-full bg-transparent outline-none text-sm font-medium text-gray-900" data-id="element-2291" />
                    </Field>
                    <Field label="CVV" data-id="element-2292">
                      <input value={cvv} onChange={e => setCvv(e.target.value)} className="w-full bg-transparent outline-none text-sm font-medium text-gray-900" type="password" data-id="element-2293" />
                    </Field>
                  </div>
                </motion.div>}

              {method === 'apple' && <motion.div initial={{
              opacity: 0,
              y: 6
            }} animate={{
              opacity: 1,
              y: 0
            }} className="mt-5 p-4 rounded-2xl border border-gray-200 bg-gray-50 text-center" data-id="element-2294">
                  <p className="text-sm text-gray-700" data-id="element-2295">
                    Confirm with Face ID on your iPhone when you press Pay.
                  </p>
                </motion.div>}

              {method === 'tabby' && <motion.div initial={{
              opacity: 0,
              y: 6
            }} animate={{
              opacity: 1,
              y: 0
            }} className="mt-5 grid grid-cols-4 gap-2" data-id="element-2296">
                  {[0, 1, 2, 3].map(i => <div key={i} className="text-center px-2 py-3 rounded-xl border border-gray-200 bg-white" data-id="element-2297">
                      <p className="text-[10px] text-gray-500 font-semibold uppercase tracking-wide" data-id="element-2298">
                        {['Today', 'In 1 mo', 'In 2 mo', 'In 3 mo'][i]}
                      </p>
                      <p className="text-sm font-bold text-gray-900 mt-1" data-id="element-2299">
                        AED {Math.round(total / 4)}
                      </p>
                    </div>)}
                </motion.div>}

              {method === 'gpay' && <motion.div initial={{
              opacity: 0,
              y: 6
            }} animate={{
              opacity: 1,
              y: 0
            }} className="mt-5 p-4 rounded-2xl border border-gray-200 bg-gray-50 flex items-center justify-between" data-id="element-2300">
                  <div className="flex items-center gap-3" data-id="element-2301">
                    <div className="w-10 h-10 rounded-full bg-white border border-gray-200 flex items-center justify-center shadow-sm" data-id="element-2302">
                      <span className="text-base font-bold leading-none" data-id="element-2303">
                        <span className="text-[#4285F4]" data-id="element-2304">G</span>
                        <span className="text-[#EA4335]" data-id="element-2305">o</span>
                      </span>
                    </div>
                    <div data-id="element-2306">
                      <p className="text-xs text-gray-500" data-id="element-2307">Google Pay</p>
                      <p className="text-sm font-bold text-gray-900" data-id="element-2308">
                        Visa •••• 4242
                      </p>
                    </div>
                  </div>
                  <span className="text-[10px] font-bold bg-blue-100 text-blue-700 px-2 py-1 rounded-full uppercase tracking-wide" data-id="element-2309">
                    Default
                  </span>
                </motion.div>}
            </div>

            {/* Promo */}
            <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6" data-id="element-2310">
              <h3 className="font-bold text-gray-900 mb-3" data-id="element-2311">Promo code</h3>
              <div className="flex gap-2" data-id="element-2312">
                <input value={promo} onChange={e => setPromo(e.target.value)} placeholder="Enter code (try MOVE30)" className="flex-1 px-4 py-3 rounded-xl border border-gray-200 outline-none text-sm focus:border-brand-300" disabled={promoApplied} data-id="element-2313" />
                <button onClick={() => {
                if (promo.trim().toUpperCase() === 'MOVE30') {
                  setPromoApplied(true);
                }
              }} disabled={!promo.trim() || promoApplied} className="px-5 py-3 bg-gray-900 hover:bg-black disabled:opacity-40 text-white rounded-xl font-semibold text-sm transition-colors" data-id="element-2314">
                  {promoApplied ? 'Applied' : 'Apply'}
                </button>
              </div>
              {promoApplied && <p className="text-xs text-green-700 mt-2 flex items-center gap-1" data-id="element-2315">
                  <CheckCircle2 className="w-3.5 h-3.5" data-id="element-2316" />
                  MOVE30 applied — AED 30 off
                </p>}
            </div>

            <motion.div layout transition={{
            type: 'spring',
            damping: 22,
            stiffness: 240
          }} className={`relative overflow-hidden rounded-3xl border-2 transition-colors duration-500 ${insured ? 'border-green-300 bg-gradient-to-br from-green-50 to-white' : 'border-amber-200 bg-gradient-to-br from-amber-50/70 to-white'}`} data-id="element-2317">
              <div className={`absolute -top-16 -right-16 w-40 h-40 rounded-full blur-3xl pointer-events-none transition-colors duration-500 ${insured ? 'bg-green-200/60' : 'bg-amber-200/50'}`} data-id="element-2318" />
              <div className="relative p-5" data-id="element-2319">
                <div className="flex items-start gap-4" data-id="element-2320">
                  <motion.div key={insured ? 'on' : 'off'} initial={{
                  scale: 0.8,
                  rotate: -15,
                  opacity: 0
                }} animate={{
                  scale: 1,
                  rotate: 0,
                  opacity: 1
                }} className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 shadow-md ${insured ? 'bg-green-500 text-white' : 'bg-amber-500 text-white'}`} data-id="element-2321">
                    {insured ? <ShieldCheck className="w-6 h-6" data-id="element-2322" /> : <ShieldAlert className="w-6 h-6" data-id="element-2323" />}
                  </motion.div>
                  <div className="flex-1 min-w-0" data-id="element-2324">
                    <div className="flex items-center gap-2 mb-1 flex-wrap" data-id="element-2325">
                      <h3 className="font-bold text-gray-900" data-id="element-2326">
                        {insured ? 'Move protection added' : 'Add move protection'}
                      </h3>
                      <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${insured ? 'bg-green-100 text-green-800 border-green-200' : 'bg-amber-100 text-amber-800 border-amber-200'}`} data-id="element-2327">
                        {insured ? <>
                            <CheckCircle2 className="w-3 h-3" data-id="element-2328" />
                            Protected
                          </> : <>
                            <AlertTriangle className="w-3 h-3" data-id="element-2329" />
                            At risk
                          </>}
                      </span>
                    </div>
                    <p className={`text-xs leading-relaxed ${insured ? 'text-green-900' : 'text-amber-900'}`} data-id="element-2330">
                      {insured ? 'Your load is fully covered for damage, theft, and delays up to AED 50,000 — and we handle claims for you.' : 'Without protection, any damage, loss, or delays during the move are not covered. Pair2 cannot reimburse claims.'}
                    </p>
                  </div>
                  <button role="switch" aria-checked={insured} onClick={() => setInsured(v => !v)} className={`relative w-12 h-7 rounded-full transition-colors shrink-0 ${insured ? 'bg-green-500' : 'bg-gray-300'}`} data-id="element-2331">
                    <motion.span layout transition={{
                    type: 'spring',
                    stiffness: 700,
                    damping: 30
                  }} className={`absolute top-0.5 w-6 h-6 rounded-full bg-white shadow-md ${insured ? 'left-[22px]' : 'left-0.5'}`} data-id="element-2332" />
                  </button>
                </div>
                <AnimatePresence initial={false} data-id="element-2333">
                  {insured && <motion.div initial={{
                  opacity: 0,
                  height: 0
                }} animate={{
                  opacity: 1,
                  height: 'auto'
                }} exit={{
                  opacity: 0,
                  height: 0
                }} className="overflow-hidden" data-id="element-2334">
                      <div className="mt-4 pt-4 border-t border-green-200/60 grid grid-cols-2 gap-2" data-id="element-2335">
                        {['Damage up to AED 50k', 'Theft & loss covered', 'Delay compensation', 'Claims handled by us'].map(item => <div key={item} className="flex items-center gap-1.5 text-[11px] font-semibold text-green-900" data-id="element-2336">
                            <CheckCircle2 className="w-3.5 h-3.5 text-green-600 shrink-0" data-id="element-2337" />
                            <span data-id="element-2338">{item}</span>
                          </div>)}
                      </div>
                    </motion.div>}
                </AnimatePresence>
                <div className="mt-4 flex items-end justify-between" data-id="element-2339">
                  <p className={`text-[11px] font-medium ${insured ? 'text-green-700' : 'text-amber-800'}`} data-id="element-2340">
                    {insured ? '✓ Coverage active the moment payment clears' : 'Recommended — keep your move worry-free'}
                  </p>
                  <p className="text-right" data-id="element-2341">
                    <span className="text-[10px] uppercase tracking-wider font-bold text-gray-500" data-id="element-2342">
                      {insured ? 'Added' : 'From'}
                    </span>
                    <br data-id="element-2343" />
                    <span className={`font-display font-bold text-lg ${insured ? 'text-green-700' : 'text-gray-900'}`} data-id="element-2344">
                      +AED {insuranceFee}
                    </span>
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Policy */}
            <div className="bg-blue-50/60 border border-blue-100 rounded-2xl p-4 flex gap-3" data-id="element-2345">
              <Shield className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" data-id="element-2346" />
              <div className="text-xs text-blue-900 leading-relaxed" data-id="element-2347">
                <p className="font-semibold mb-1" data-id="element-2348">Cancellation policy</p>
                <p data-id="element-2349">
                  Free cancellation up to 30 minutes before pickup. After that,
                  a 20% fee applies. You can cancel any time from{' '}
                  <span className="font-semibold" data-id="element-2350">My Jobs</span>.
                </p>
              </div>
            </div>
          </div>

          {/* ORDER SUMMARY */}
          <div className="lg:col-span-2 space-y-4" data-id="element-2351">
            {(statePrompt || photos.length > 0) && <RequestBrief prompt={statePrompt} serviceName={service.name} photos={photos} compact data-id="element-2352" />}
            <motion.div animate={{
            boxShadow: insured ? '0 0 0 2px rgba(34, 197, 94, 0.35), 0 10px 40px -10px rgba(34, 197, 94, 0.25)' : '0 0 0 0px rgba(0,0,0,0), 0 0 0 0px rgba(0,0,0,0)'
          }} transition={{
            duration: 0.4
          }} className={`bg-white rounded-3xl border shadow-sm sticky top-24 overflow-hidden ${insured ? 'border-green-200' : 'border-gray-100'}`} data-id="element-2353">
              {/* Safety ribbon — only when insured */}
              <AnimatePresence data-id="element-2354">
                {insured && <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-5 py-2.5 flex items-center gap-2" data-id="element-2355">
                    <ShieldCheck className="w-4 h-4" data-id="element-2356" />
                    <p className="text-xs font-bold uppercase tracking-wider" data-id="element-2357">
                      Safe move · Fully protected by Pair2
                    </p>
                    <Sparkles className="w-3.5 h-3.5 ml-auto" data-id="element-2358" />
                  </motion.div>}
              </AnimatePresence>
              <div className="p-6" data-id="element-2359">
                <h3 className="font-bold text-gray-900 mb-4" data-id="element-2360">Order summary</h3>

                <div className="bg-gray-50 rounded-2xl p-4 mb-5" data-id="element-2361">
                  <div className="flex items-center gap-2 mb-3" data-id="element-2362">
                    <Truck className="w-4 h-4 text-brand-500" data-id="element-2363" />
                    <p className="font-semibold text-gray-900 text-sm" data-id="element-2364">
                      {supplier.name}
                    </p>
                  </div>
                  <p className="text-xs text-gray-500 mb-3" data-id="element-2365">{supplier.truck}</p>
                  <div className="space-y-2" data-id="element-2366">
                    <div className="flex items-start gap-2 text-xs text-gray-600" data-id="element-2367">
                      <MapPin className="w-3.5 h-3.5 text-brand-500 mt-0.5 shrink-0" data-id="element-2368" />
                      <span className="leading-snug" data-id="element-2369">
                        <span className="text-gray-500" data-id="element-2370">From </span>
                        <span className="font-medium text-gray-900" data-id="element-2371">
                          {service.summary.pickup}
                        </span>
                        <br data-id="element-2372" />
                        <span className="text-gray-500" data-id="element-2373">To </span>
                        <span className="font-medium text-gray-900" data-id="element-2374">
                          {service.summary.dropoff}
                        </span>
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-600" data-id="element-2375">
                      <Clock className="w-3.5 h-3.5 text-gray-400" data-id="element-2376" />
                      <span data-id="element-2377">{service.summary.pickupTime}</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-2.5 mb-4 text-sm" data-id="element-2378">
                  <Row label="Subtotal" value={`AED ${subtotal}`} data-id="element-2379" />
                  <Row label="Service fee (5%)" value={`AED ${serviceFee}`} data-id="element-2380" />
                  {insured && <Row label={<span className="inline-flex items-center gap-1.5" data-id="element-2382">
                          <ShieldCheck className="w-3.5 h-3.5 text-green-600" data-id="element-2383" />
                          Move protection
                        </span>} value={`AED ${insuranceFee}`} accent="text-green-700 font-semibold" data-id="element-2381" />}
                  <Row label="VAT (5%)" value={`AED ${vat}`} data-id="element-2384" />
                  {promoApplied && <Row label="Promo (MOVE30)" value={`– AED ${promoDiscount}`} accent="text-green-700" data-id="element-2385" />}
                </div>

                <div className="border-t border-gray-100 pt-4 mb-5 flex justify-between items-end" data-id="element-2386">
                  <p className="text-sm font-medium text-gray-500" data-id="element-2387">Total</p>
                  <p className="text-3xl font-display font-bold text-gray-900" data-id="element-2388">
                    AED {total}
                  </p>
                </div>

                <button onClick={handlePay} disabled={processing} className={`w-full py-4 text-white rounded-2xl font-bold text-base transition-colors shadow-md flex items-center justify-center gap-2 disabled:opacity-60 ${insured ? 'bg-green-600 hover:bg-green-700 shadow-green-500/25' : 'bg-brand-500 hover:bg-brand-600 shadow-brand-500/20'}`} data-id="element-2389">
                  {processing ? <>
                      <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" data-id="element-2390" />
                      Processing...
                    </> : <>
                      {insured ? <ShieldCheck className="w-4 h-4" data-id="element-2391" /> : <Lock className="w-4 h-4" data-id="element-2392" />}
                      Pay AED {total} &{' '}
                      {insured ? 'start safe move' : 'start job'}
                    </>}
                </button>

                <p className="text-[10px] text-center text-gray-400 mt-3 font-medium tracking-wide" data-id="element-2393">
                  Secured by Pair2 Pay · 256-bit SSL
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>;
}
function MethodPill({
  active,
  icon,
  label,
  onClick
}: {
  active: boolean;
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}) {
  return <button onClick={onClick} className={`flex flex-col items-center justify-center gap-1.5 py-3 px-2 rounded-2xl border-2 transition-all ${active ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-gray-100 bg-white hover:border-gray-200 text-gray-700'}`} data-id="element-2394">
      {icon}
      <span className="text-[11px] font-semibold" data-id="element-2395">{label}</span>
    </button>;
}
function Field({
  label,
  children
}: {
  label: string;
  children: React.ReactNode;
}) {
  return <div className="px-4 py-2.5 rounded-xl border border-gray-200 bg-white focus-within:border-brand-300 transition-colors" data-id="element-2396">
      <p className="text-[10px] text-gray-500 uppercase tracking-wide font-semibold mb-0.5" data-id="element-2397">
        {label}
      </p>
      {children}
    </div>;
}
function Row({
  label,
  value,
  accent
}: {
  label: React.ReactNode;
  value: string;
  accent?: string;
}) {
  return <div className="flex justify-between text-gray-600" data-id="element-2398">
      <span data-id="element-2399">{label}</span>
      <span className={`font-medium ${accent || 'text-gray-900'}`} data-id="element-2400">
        {value}
      </span>
    </div>;
}